export type LoginFinalizeNavigation =
  | { kind: "continue" }
  | { kind: "choose-organization" }
  | { kind: "unsupported-task" }

export type LoginSessionSurface =
  | { kind: "choose-organization" }
  | { kind: "unsupported-task" }
  | { kind: "active-session" }
  | { kind: "password" }
  | { kind: "verification" }
  | { kind: "reset-request" }
  | { kind: "reset-confirm" }
  | { kind: "reset-link" }

export type LoginStep = "password" | "verification" | "reset-request" | "reset-confirm" | "reset-link" | "choose-organization"

export type ChooseWorkspaceMembership = {
  id: string
  organizationId: string
  organizationName: string
}

export type ChooseWorkspaceViewState =
  | { status: "loading"; message: string }
  | { status: "error"; message: string }
  | { status: "empty"; message: string }
  | { status: "ready"; memberships: ChooseWorkspaceMembership[] }

type UserMembershipsState = {
  isLoading?: boolean
  isFetching?: boolean
  hasNextPage?: boolean
  isError?: boolean
  error?: unknown
  data?: unknown
  fetchNext?: () => void
} | null | undefined

export const UNSUPPORTED_SESSION_TASK_MESSAGE =
  "This account needs one more setup step before it can open the workspace."

export function resolveLoginFinalizeNavigation(
  session?: { currentTask?: unknown } | null,
): LoginFinalizeNavigation {
  const task = session?.currentTask
  if (task == null) return { kind: "continue" }

  if (
    typeof task === "object"
    && "key" in task
    && (task as { key?: unknown }).key === "choose-organization"
  ) {
    return { kind: "choose-organization" }
  }

  return { kind: "unsupported-task" }
}

export function resolveLoginSessionSurface(input: {
  isAuthLoaded: boolean
  isSignedIn: boolean
  session?: { currentTask?: unknown } | null
  localStep: LoginStep
}): LoginSessionSurface {
  if (input.isAuthLoaded) {
    const taskNavigation = resolveLoginFinalizeNavigation(input.session)
    if (taskNavigation.kind === "choose-organization") return { kind: "choose-organization" }
    if (taskNavigation.kind === "unsupported-task") return { kind: "unsupported-task" }
    if (input.isSignedIn) return { kind: "active-session" }
  }

  return { kind: input.localStep }
}

/**
 * Latch for automatic signed-in handoff on /login.
 * Only a session that is already active when auth first loads may auto-continue.
 * Local password/MFA sign-in must stay on finalizeSignIn (never flip pending → started later).
 */
export type ExistingSessionHandoffPhase = "pending" | "started" | "skipped"

export function nextExistingSessionHandoffPhase(input: {
  phase: ExistingSessionHandoffPhase
  isAuthLoaded: boolean
  sessionSurfaceKind: LoginSessionSurface["kind"]
}): ExistingSessionHandoffPhase {
  if (input.phase !== "pending") return input.phase
  if (!input.isAuthLoaded) return "pending"
  return input.sessionSurfaceKind === "active-session" ? "started" : "skipped"
}

export function shouldRequestNextMembershipPage(input: {
  isLoaded: boolean
  userMemberships: UserMembershipsState
}): boolean {
  if (!input.isLoaded || !input.userMemberships) return false
  if (typeof input.userMemberships.fetchNext !== "function") return false
  if (input.userMemberships.error || input.userMemberships.isError) return false
  if (input.userMemberships.isLoading || input.userMemberships.isFetching) return false
  return Boolean(input.userMemberships.hasNextPage)
}

export function buildChooseWorkspaceView(input: {
  isLoaded: boolean
  userMemberships: UserMembershipsState
}): ChooseWorkspaceViewState {
  if (!input.isLoaded || !input.userMemberships) {
    return { status: "loading", message: "Loading workspaces." }
  }

  if (input.userMemberships.error || input.userMemberships.isError) {
    return { status: "error", message: "Unable to load workspaces." }
  }

  if (
    input.userMemberships.isLoading
    || input.userMemberships.isFetching
    || input.userMemberships.hasNextPage
  ) {
    return { status: "loading", message: "Loading workspaces." }
  }

  const memberships = readChooseWorkspaceMemberships(input.userMemberships.data)
  if (memberships.length === 0) {
    return {
      status: "empty",
      message: "This account does not have access to a workspace.",
    }
  }

  return { status: "ready", memberships }
}

export async function activateChosenOrganization(input: {
  organizationId: string
  redirectUrl: string
  setActive: (params: { organization: string }) => Promise<unknown>
  assignLocation: (url: string) => void
}): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await input.setActive({ organization: input.organizationId })
    input.assignLocation(input.redirectUrl)
    return { ok: true }
  } catch {
    return { ok: false, message: "Unable to sign in." }
  }
}

/** Workspaces whose name contains the query, ignoring case. A blank query keeps them all. */
export function filterChooseWorkspaceMemberships(
  memberships: ChooseWorkspaceMembership[],
  query: string,
): ChooseWorkspaceMembership[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return memberships
  return memberships.filter((membership) => membership.organizationName.toLowerCase().includes(needle))
}

function readChooseWorkspaceMemberships(data: unknown): ChooseWorkspaceMembership[] {
  if (!Array.isArray(data)) return []

  const memberships: ChooseWorkspaceMembership[] = []
  for (const entry of data) {
    if (!entry || typeof entry !== "object") continue
    const record = entry as {
      id?: unknown
      organization?: { id?: unknown; name?: unknown } | null
    }
    if (typeof record.id !== "string" || !record.id) continue
    if (!record.organization || typeof record.organization !== "object") continue
    if (typeof record.organization.id !== "string" || !record.organization.id) continue
    if (typeof record.organization.name !== "string" || !record.organization.name) continue

    memberships.push({
      id: record.id,
      organizationId: record.organization.id,
      organizationName: record.organization.name,
    })
  }

  return memberships
}
